/**
 * Upgrade-page link handling: deep-link params, attribution, the known-offer
 * display table, and the sessionStorage intent that survives the OAuth relay
 * round trip. Pure logic, no React — see marketing-hq/closer/upgrade-signin-spec.md.
 */

export type PlanKey = 'monthly' | 'yearly';

// ── Deep-link params (?plan=yearly&promo=YEAR25) ─────────────────────────────

/** Shape-check only; whether a code is real is the backend's call. */
const PROMO_CODE_RE = /^[A-Z0-9]{3,20}$/;

export interface UpgradeParams {
  /** Plan to preselect; null keeps the page default. */
  plan: PlanKey | null;
  /** Valid-looking promo, or null. */
  promo: string | null;
}

const isPlanKey = (v: unknown): v is PlanKey => v === 'monthly' || v === 'yearly';

export const parseUpgradeParams = (search: string): UpgradeParams => {
  const params = new URLSearchParams(search);
  const rawPlan = params.get('plan');
  const rawPromo = params.get('promo');
  return {
    plan: isPlanKey(rawPlan) ? rawPlan : null,
    promo: rawPromo !== null && PROMO_CODE_RE.test(rawPromo) ? rawPromo : null,
  };
};

// ── Attribution (spec §8) ────────────────────────────────────────────────────

const ATTRIBUTION_RE = /^[a-z0-9_-]{1,32}$/;

const sanitizeLabel = (raw: string | null): string | null => {
  if (raw === null) return null;
  const v = raw.trim().toLowerCase();
  return ATTRIBUTION_RE.test(v) ? v : null;
};

export interface UpgradeAttribution {
  /** 'app' for a `ct` arrival, else the sanitised utm_source, else 'direct'. */
  source: string;
  utmCampaign: string | null;
}

/** Must run before the ct effect or the relay rewrites the URL. */
export const parseAttribution = (search: string): UpgradeAttribution => {
  const params = new URLSearchParams(search);
  return {
    source: params.get('ct') ? 'app' : sanitizeLabel(params.get('utm_source')) ?? 'direct',
    utmCampaign: sanitizeLabel(params.get('utm_campaign')),
  };
};

// ── Known offers (display only; the backend allowlist + Stripe decide the charge) ──

export type OfferI18nKey = 'founding' | 'year25';

export interface KnownOffer {
  plan: PlanKey;
  /** account:upgrade.offer.<i18n>.name */
  i18n: OfferI18nKey;
  /** USD, the same string in every locale (Stripe checkout is USD). */
  firstYear: string;
  /** ISO instant the offer stops being shown; null = no end date. */
  endsAt: string | null;
}

export const KNOWN_OFFERS: Readonly<Record<string, KnownOffer>> = {
  // 23:59 ET Oct 25 — matches the Stripe promotion code's expires_at (extended 10-17, promo_1URPMP90niUpPTKLDrkzQmeU).
  FOUNDING: { plan: 'yearly', i18n: 'founding', firstYear: '$24.99', endsAt: '2026-10-26T03:59:00Z' },
  YEAR25: { plan: 'yearly', i18n: 'year25', firstYear: '$29.99', endsAt: null },
};

export type OfferState =
  /** No promo, an invalid one, or an unknown code that belongs to the other plan. */
  | { kind: 'none' }
  /** Known, live, on its plan: show the offer price and send the code. */
  | { kind: 'live'; code: string; offer: KnownOffer }
  /** Known but past endsAt: regular price + the ended line; code NOT sent. */
  | { kind: 'ended'; code: string; offer: KnownOffer }
  /** Known and live, but the visitor selected the other plan: code NOT sent. */
  | { kind: 'otherPlan'; code: string; offer: KnownOffer }
  /** Shape-valid but not in the table (e.g. a private code): sent, never displayed. */
  | { kind: 'unknown'; code: string };

export const resolveOffer = (link: UpgradeParams, plan: PlanKey, now: number): OfferState => {
  const code = link.promo;
  if (!code) return { kind: 'none' };
  const offer = Object.prototype.hasOwnProperty.call(KNOWN_OFFERS, code) ? KNOWN_OFFERS[code] : undefined;
  if (!offer) {
    // A promo arrives for the plan the link named; switching plan drops it
    // rather than letting Stripe reject the session.
    return link.plan === null || link.plan === plan ? { kind: 'unknown', code } : { kind: 'none' };
  }
  if (offer.endsAt !== null && now >= Date.parse(offer.endsAt)) return { kind: 'ended', code, offer };
  if (offer.plan !== plan) return { kind: 'otherPlan', code, offer };
  return { kind: 'live', code, offer };
};

/** The code to send to createCheckoutSession, if any. */
export const promoToSend = (state: OfferState): string | null =>
  state.kind === 'live' || state.kind === 'unknown' ? state.code : null;

// ── Intent across the OAuth relay round trip (spec §7.1) ─────────────────────
// The relay returns to the BARE /upgrade URL (it accepts no query and appends
// `#relayToken=` itself), so everything the visitor chose rides here instead.

const INTENT_KEY = 'rhemn:upgrade:params';

export interface UpgradeIntent extends UpgradeAttribution {
  plan: PlanKey;
  promo: string | null;
}

export const saveUpgradeIntent = (intent: UpgradeIntent): void => {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* Storage blocked: the relay return lands on the default plan. */
  }
};

export const clearUpgradeIntent = (): void => {
  try {
    sessionStorage.removeItem(INTENT_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Non-destructive read (safe in a StrictMode-doubled state initializer); the
 * caller clears it with clearUpgradeIntent once acted on. Anything malformed
 * is treated as absent, and every field is re-validated: sessionStorage is
 * same-origin but still input.
 */
export const readUpgradeIntent = (): UpgradeIntent | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(INTENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof UpgradeIntent, unknown>>;
    if (!isPlanKey(v.plan)) return null;
    const promo = typeof v.promo === 'string' && PROMO_CODE_RE.test(v.promo) ? v.promo : null;
    const source = typeof v.source === 'string' ? sanitizeLabel(v.source) : null;
    const utmCampaign = typeof v.utmCampaign === 'string' ? sanitizeLabel(v.utmCampaign) : null;
    return { plan: v.plan, promo, source: source ?? 'direct', utmCampaign };
  } catch {
    return null;
  }
};

/**
 * Query string that carries the offer + attribution back to /upgrade (Stripe
 * cancel URL, and the cancel page's "Back to upgrade" link). Only validated
 * values are ever written; '' when there is nothing to carry.
 */
export const buildUpgradeQuery = (p: UpgradeParams & UpgradeAttribution): string => {
  const q = new URLSearchParams();
  if (p.plan) q.set('plan', p.plan);
  if (p.promo) q.set('promo', p.promo);
  // 'app' rides as utm_source so a ct visitor who backs out of Stripe is still
  // attributed to the app once the (single-use) token is gone.
  if (p.source !== 'direct') q.set('utm_source', p.source);
  if (p.utmCampaign) q.set('utm_campaign', p.utmCampaign);
  const s = q.toString();
  return s ? `?${s}` : '';
};
